import React, { useEffect, useRef } from 'react';

import { debounce } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IFrameContainer } from 'components/iframe-campaign/iframe-campaign.styled';
import {
  IBrameEventAttributes,
  IEventDetails,
  IUserIdentities,
  useTrackEventViaBeMutation,
} from 'generated/rbi-graphql';
import { useFeatureIFrame } from 'hooks/use-feature-iframe';
import LoadingAnimation from 'pages/loyalty/loyalty-opt-in-modal/loading-animation';
import NotFound from 'pages/not-found';
import { useAuthContext } from 'state/auth';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { isNative } from 'utils/environment';
import { ISOs } from 'utils/form/constants';
import { routes } from 'utils/routing';

export enum IframeCampaignAvailability {
  APP_ONLY = 'appOnly',
  WEB_AND_APP = 'webAndApp',
  WEB_ONLY = 'webOnly',
}

export enum PrizeResultTypes {
  END_GAME_OFFER = 'endGameOffer',
  END_GAME_LOYALTY_POINTS = 'endGameLoyalty',
}

const checkUrl = (urlString: string) => {
  if (!urlString) {
    return false;
  }

  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

const normalizeUrlParams = (urlString: string) => {
  const searchParams = new URL(urlString).search;

  if (searchParams) {
    return `${urlString}&`;
  }

  return `${urlString}?`;
};

const IframeCampaign: React.FunctionComponent = () => {
  const { featureIFrame, featureIFrameLoading } = useFeatureIFrame();

  const iFrameCampaignUrlWeb = featureIFrame?.iFrameCampaignUrlWeb?.locale || '';
  const iFrameCampaignUrlApp = featureIFrame?.iFrameCampaignUrlApp?.locale || '';
  const iFrameOnlyAvailableForAthenticatedUsers =
    featureIFrame?.iFrameAvailableForAthenticatedUsers;
  const enableIframeCampaignInWebOnly =
    featureIFrame?.enableIFrameComponent === IframeCampaignAvailability.WEB_ONLY;
  const enableIframeCampaignInAppOnly =
    featureIFrame?.enableIFrameComponent === IframeCampaignAvailability.APP_ONLY;
  const passLoyaltyIdToIframe = featureIFrame?.passLoyaltyIdToIframe;

  const [trackEventViaBeMutation] = useTrackEventViaBeMutation();

  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { user } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const { refetchLoyaltyUser, queryLoyaltyUserStandardOffers } = useLoyaltyContext();
  const frameRef = useRef<HTMLIFrameElement>(null);

  const notFoundConditions =
    !featureIFrameLoading &&
    ((enableIframeCampaignInWebOnly && isNative) || (enableIframeCampaignInAppOnly && !isNative));

  const isValidWebURL = checkUrl(iFrameCampaignUrlWeb);
  const isValidAppURL = checkUrl(iFrameCampaignUrlApp);

  let userId = user?.cognitoId || '';

  if (passLoyaltyIdToIframe) {
    userId = loyaltyUser?.id || '';
  }

  const iframeUrlWeb = isValidWebURL
    ? `${normalizeUrlParams(iFrameCampaignUrlWeb)}userId=${userId}`
    : '';
  const iframeUrlApp = isValidAppURL
    ? `${normalizeUrlParams(iFrameCampaignUrlApp)}userId=${userId}`
    : '';

  // Set the default iFrame URL as the one defined in App URL, unless the campaign is WebOnly
  let iFrameUrl = iframeUrlApp;

  if (enableIframeCampaignInWebOnly) {
    iFrameUrl = iframeUrlWeb;
  }

  useEffect(() => {
    frameRef.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    try {
      if (!featureIFrameLoading && !iFrameUrl) {
        throw new Error('No iFrame URL was found. Please define a valid URL');
      }

      const handleEventMessages = (event: MessageEvent) => {
        // used to deduplicate the game_started and game_finished events
        const eventData = event.data?.data;
        const eventName = event.data?.eventName;

        const prize = event.data?.prize;
        const eventResultType = event.data?.func;

        if (user?.details && loyaltyUser) {
          const eventAttributes: IBrameEventAttributes = {
            userId: user.cognitoId,
            loyaltyId: loyaltyUser.id,
            userEmail: user.details.email,
          };

          const userIdentities: IUserIdentities = {
            cognitoId: user.cognitoId,
            isoCountryCode: user.details.isoCountryCode as ISOs,
            email: user.details.email,
          };

          // Send initially the event to trigger the offer/points for the user
          if (eventResultType?.includes('prize')) {
            const eventDetails: IEventDetails = {
              eventName: prize,
              eventAttributes,
            };

            trackEventViaBeMutation({
              variables: { input: { eventDetails, userIdentities } },
            }).catch(e => {
              // eslint-disable-next-line no-console
              console.log(e);
            });
          }

          if (
            (eventName === 'game_started' || eventName === 'game_finished') &&
            eventData === null
          ) {
            const eventDetails: IEventDetails = {
              eventName,
              eventAttributes,
            };
            trackEventViaBeMutation({
              variables: { input: { eventDetails, userIdentities } },
            }).catch(e => {
              // eslint-disable-next-line no-console
              console.log(e);
            });
          }
        }

        // At the end of the game, update either the offer list or the points of the user accordingly
        if (eventResultType === PrizeResultTypes.END_GAME_OFFER) {
          // Updating Offers List
          queryLoyaltyUserStandardOffers({ loyaltyId: userId });
          const redirect = debounce(() => {
            navigate(routes.rewardsOffers);
          }, 7000);

          redirect();
          return;
        } else if (eventResultType === PrizeResultTypes.END_GAME_LOYALTY_POINTS) {
          // Updating Loyalty Points
          refetchLoyaltyUser();
          const redirect = debounce(() => {
            navigate(routes.base);
          }, 8000);

          redirect();
          return;
        }
      };

      window.addEventListener('message', handleEventMessages);
      // clean up
      return () => {
        window.removeEventListener('message', handleEventMessages);
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      navigate(routes.base);
      return;
    }
  }, [
    loyaltyUser,
    formatMessage,
    navigate,
    queryLoyaltyUserStandardOffers,
    refetchLoyaltyUser,
    trackEventViaBeMutation,
    user,
    userId,
    featureIFrameLoading,
    iFrameUrl,
  ]);

  if (notFoundConditions) {
    return <NotFound />;
  }

  if (iFrameOnlyAvailableForAthenticatedUsers && !user) {
    navigate(routes.base);
    return null;
  }

  return featureIFrameLoading ? (
    <LoadingAnimation />
  ) : (
    <IFrameContainer>
      <iframe
        ref={frameRef}
        title="iframe-campaign"
        data-testid="iframe-campaign"
        id="iframe-campaign"
        src={iFrameUrl}
        style={{ height: '96vh' }}
        frameBorder="0"
        width="100%"
        height="100%"
      />
    </IFrameContainer>
  );
};

export default IframeCampaign;
